import { ReactNode, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useMsal } from '@azure/msal-react';

import { getMsalInstance } from './msalInstanceManager';
import { AuthLoadingState } from 'src/components/LoadingState';
import { TokenAutoRefresh } from 'src/components/TokenAutoRefresh';
import { SessionExpirationWarning } from 'src/components/SessionExpirationWarning';
import { CustomLoginPage } from 'src/components/auth/CustomLoginPage';
import { RedirectProcessingScreen } from 'src/components/auth/RedirectProcessingScreen';
import { isRedirectReturn } from 'src/utils/authStateManager';
import { loginRequest } from './msalConfig';
import { useMagicLinkRequest } from 'src/queries/auth/useMagicLinkRequestMutation';
import { toast } from 'react-toastify';

interface EntraAuthProviderProps {
  readonly children: ReactNode;
}

/**
 * Enhanced authentication flow manager that handles MSAL state and UI logic
 * Supports both popup and redirect flows with branded login experience
 * Renamed from EntraAuthContent to better reflect its responsibility as a flow controller
 */
function EntraAuthManager({ children }: EntraAuthProviderProps) {
  const { inProgress, instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const magicLinkRequest = useMagicLinkRequest();
  const isHandlingRedirect = isRedirectReturn();

  // Handle redirect response on component mount
  useEffect(() => {
    const handleRedirectResponse = async () => {
      if (isHandlingRedirect) {
        try {
          console.log('🔄 Processing redirect response...');
          const response = await instance.handleRedirectPromise();
          if (response) {
            console.log(
              '✅ Redirect response processed successfully:',
              response.account?.username,
            );
          } else {
            console.log('ℹ️ No redirect response to process');
          }
        } catch (error) {
          console.error('❌ Error processing redirect response:', error);
          setAuthError(
            error instanceof Error ? error.message : 'Authentication failed',
          );

          // If there's an invalid resource error, clear the cache and force fresh auth
          if (
            error instanceof Error &&
            error.message.includes('invalid_resource')
          ) {
            console.log(
              '🧹 Clearing MSAL cache due to invalid resource error...',
            );
            await instance.clearCache();
          }
        }
      }
    };

    handleRedirectResponse();
  }, [instance, isHandlingRedirect]);

  // Helper function to determine what to render in unauthenticated state
  const renderUnauthenticatedContent = () => {
    // Handle OAuth redirect processing first (highest priority)
    if (isHandlingRedirect) {
      return (
        <RedirectProcessingScreen
          message="Completing authentication..."
          submessage="Processing your sign-in response"
        />
      );
    }

    // More granular MSAL state handling for better UX
    const isInitializing =
      inProgress === 'startup' || inProgress === 'handleRedirect';
    const isInteracting =
      inProgress === 'login' || inProgress === 'acquireToken';

    if (isInitializing) {
      return (
        <AuthLoadingState
          provider="Microsoft"
          message="Initializing authentication..."
        />
      );
    }

    if (isInteracting) {
      return (
        <AuthLoadingState
          provider="Microsoft"
          message="Processing authentication..."
        />
      );
    }

    // Show login page when MSAL is ready and not processing
    return (
      <CustomLoginPage
        authProvider="entra"
        onLogin={handleLoginClick}
        onMagicLinkLogin={handleMagicLinkLogin}
        isLoading={isLoading}
        error={authError}
      />
    );
  };

  const handleMagicLinkLogin = async (email: string) => {
    try {
      setIsLoading(true);
      await magicLinkRequest.mutateAsync(
        { email },
        {
          onSuccess: (data) => {
            toast.success(data);
            navigate(
              `/magic-link/enter-code?email=${encodeURIComponent(email)}`,
            );
          },
          onError: (error) => {
            toast.error(
              error instanceof Error
                ? error.message
                : 'Failed to send login code',
            );
          },
        },
      );
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
    return true;
  };

  // Simple redirect login handler with cache clearing
  const handleLoginClick = async () => {
    try {
      setIsLoading(true);
      setAuthError(undefined);

      // Clear any existing cache to ensure fresh authentication
      console.log('🧹 Clearing MSAL cache before login...');
      await instance.clearCache();

      console.log(
        '🔄 Starting login redirect with scopes:',
        loginRequest.scopes,
      );
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('❌ Login failed:', error);
      setAuthError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Determine authentication state manually to prevent both templates from rendering
  // If we have accounts but no active account, set the first account as active.
  // We only do this if we are not handling a redirect return or processing redirect.
  const isInitializingOrRedirecting =
    isHandlingRedirect ||
    inProgress === 'startup' ||
    inProgress === 'handleRedirect';

  if (
    !isInitializingOrRedirecting &&
    accounts.length > 0 &&
    !instance.getActiveAccount()
  ) {
    instance.setActiveAccount(accounts[0]);
  }

  const isAuthenticated = accounts.length > 0;
  const isInitializing =
    inProgress === 'startup' || inProgress === 'handleRedirect';

  // Show loading during initialization to prevent template conflicts
  if (isInitializing) {
    return (
      <AuthLoadingState
        provider="Microsoft"
        message="Initializing authentication..."
      />
    );
  }

  // Manual authentication state control instead of relying on MSAL React templates
  if (isAuthenticated) {
    return (
      <>
        {children}
        <TokenAutoRefresh />
        <SessionExpirationWarning />
      </>
    );
  }

  // User is not authenticated - show login page
  return renderUnauthenticatedContent();
}

/**
 * Entra ID Authentication Provider Component
 * Wraps the application with MSAL provider and handles authentication flow
 */
export function EntraAuthProvider({ children }: EntraAuthProviderProps) {
  try {
    // MsalProvider is mounted in main.tsx so useAuth()/useMsal() work above this tree.
    getMsalInstance();

    return <EntraAuthManager>{children}</EntraAuthManager>;
  } catch (error) {
    console.error('❌ Failed to create MSAL instance:', error);

    // Return a fallback UI instead of crashing
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-red-600 mb-4">
              Authentication Configuration Error
            </h2>
            <p className="text-gray-600 mb-4">
              There was an error initializing the authentication system.
            </p>
            <p className="text-sm text-gray-500">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default EntraAuthProvider;
